// src/components/Chat.js
import React, { useEffect, useState, useRef } from "react";
import {
  Send,
  Paperclip,
  Search,
  X,
  MessageSquare,
  Trash2,
  Edit3,
  Reply,
  CornerUpLeft,
  Check,
  CheckCheck,
} from "lucide-react";

// WebRTC and screen share variables (reserved for future)
let localPeer = null;
let localStream = null;
let currentSessionId = null;

export default function Chat() {
  const [conversations, setConversations] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingConvs, setLoadingConvs] = useState(true);
  const [composing, setComposing] = useState("");
  const [fileInput, setFileInput] = useState(null);
  const [sending, setSending] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  


  // Context menu state
  const [contextMenu, setContextMenu] = useState(null);
  const [contextMessage, setContextMessage] = useState(null);
  const [messageToDelete, setMessageToDelete] = useState(null);

  // Conversation context menu state
  const [convContextMenu, setConvContextMenu] = useState(null);
  const [convToDelete, setConvToDelete] = useState(null);
  const [convDeleteConfirm, setConvDeleteConfirm] = useState(false);

  // Edit mode state
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editingText, setEditingText] = useState("");

  // Reply mode state
  const [replyTo, setReplyTo] = useState(null);

  // Modal states
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [groupName, setGroupName] = useState("");
  const [selectedGroupMemberIds, setSelectedGroupMemberIds] = useState([]);
  const [showAddMembersModal, setShowAddMembersModal] = useState(false);
  const [selectedAddMemberIds, setSelectedAddMemberIds] = useState([]);

  // Mobile sidebar state
  const [showSidebar, setShowSidebar] = useState(true);

  // Highlighted message state (for scroll-to-message)
  const [highlightedMessageId, setHighlightedMessageId] = useState(null);

  // Screen sharing state (reserved / partial)
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [screenStream, setScreenStream] = useState(null);
  const [isScreenShareModalOpen, setIsScreenShareModalOpen] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const screenVideoRef = useRef(null);
  const screenShareContainerRef = useRef(null);

  // Screen share request state (reserved / partial)
  const [screenShareRequest, setScreenShareRequest] = useState(null);
  const [isWaitingForAcceptance, setIsWaitingForAcceptance] = useState(false);
  const [incomingScreenShare, setIncomingScreenShare] = useState(null);

  const currentUser = JSON.parse(localStorage.getItem("currentUser") || "{}");
  const currentEmail = currentUser?.email || "";
  const currentUserId = currentUser?.id || null;

  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const [users, setUsers] = useState([]);

  // ===== INITIAL LOAD =====
  useEffect(() => {
    loadConversations();
  }, []);

  useEffect(() => {
    loadUsers();
  }, []);

  // Scroll to bottom on messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Periodically refresh users and conversations so new users show in the sidebar
  useEffect(() => {
    const interval = setInterval(() => {
      loadUsers();
      loadConversations();
    }, 10000); // every 10 seconds

    return () => clearInterval(interval);
  }, []);


  // Close context menu on click outside
  useEffect(() => {
    const handleClickOutside = () => {
      setContextMenu(null);
      setContextMessage(null);
      setConvContextMenu(null);
      // do not clear convToDelete so confirmation can proceed if already open
    };
    window.addEventListener("click", handleClickOutside);
    return () => window.removeEventListener("click", handleClickOutside);
  }, []);

  // 🔁 Live updates: poll messages for active conversation
  useEffect(() => {
    if (!activeConv?.id) return;

    let isMounted = true;

    const fetchLatestMessages = async () => {
      try {
        const res = await fetch(
          `https://aaapd.org.et/gallery_backend/api/get_messages.php?conversationId=${activeConv.id}&userId=${currentUserId}`
        );
        const msgs = await res.json();

        if (!Array.isArray(msgs) || !isMounted) return;

        setMessages((prev) => {
          if (prev.length === msgs.length) {
            let identical = true;
            for (let i = 0; i < prev.length; i++) {
              if (
                prev[i].id !== msgs[i].id ||
                prev[i].text !== msgs[i].text ||
                prev[i].edited !== msgs[i].edited
              ) {
                identical = false;
                break;
              }
            }
            if (identical) return prev;
          }
          return msgs;
        });
      } catch (err) {
        console.error("Polling messages failed:", err);
      }
    };

    // Fetch immediately, then every 3 seconds
    fetchLatestMessages();
    const interval = setInterval(fetchLatestMessages, 3000);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [activeConv?.id, currentUserId]);

  // ===== HELPERS =====

  async function ensureGlobalGroupConversation() {
    try {
      // Ensure we have a valid userId (email may have changed)
      let uid = currentUserId;
      if (!uid) {
        uid = await resolveCurrentUserId();
        if (!uid) return;
      }

      // First, check if a Global Group conversation already exists for this user
      const convRes = await fetch(
        `https://aaapd.org.et/gallery_backend/api/get_conversations.php?userId=${uid}`
      );
      const existingConvs = await convRes.json();
      const hasGlobal =
        Array.isArray(existingConvs) &&
        existingConvs.some(
          (c) => (c?.name || "").toLowerCase() === "global group"
        );
      if (hasGlobal) return; // Do not create another global group

      // If not present, create once with all current users
      const res = await fetch(
        `https://aaapd.org.et/gallery_backend/api/get_users.php`
      );
      const allUsers = await res.json();
      if (!Array.isArray(allUsers) || allUsers.length === 0) return;

      const memberIds = Array.from(
        new Set(allUsers.map((u) => u.id).filter(Boolean).concat([uid]))
      );

      await fetch(
        "https://aaapd.org.et/gallery_backend/api/create_conversation.php",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({ memberIds, name: "Global Group" }),
        }
      ).catch(() => {});
    } catch (_) {}
  }

  async function loadConversations() {
    setLoadingConvs(true);
    try {
      // Ensure global group exists first
      let uid = currentUserId || (await resolveCurrentUserId());
      if (uid) {
        await ensureGlobalGroupConversation();
      } else {
        throw new Error("No user id");
      }

      const res = await fetch(
        `https://aaapd.org.et/gallery_backend/api/get_conversations.php?userId=${uid}`
      );
      let convs = await res.json();

      if (!Array.isArray(convs)) convs = [];

      // Sort by lastMessageAt descending
      convs.sort(
        (a, b) => new Date(b.lastMessageAt) - new Date(a.lastMessageAt)
      );


      // Avoid duplicate Global Group entries per user (can happen when new members join)
      let seenGlobal = false;
      const deduped = convs.filter((c) => {
        const isGlobal = (c?.name || "").toLowerCase() === "global group";
        if (!isGlobal) return true;
        if (seenGlobal) return false;
        seenGlobal = true;
        return true;
      });

      setConversations(deduped);
    } catch (err) {
      console.error("Failed to load conversations:", err);
    } finally {
      setLoadingConvs(false);
    }
  }

  async function loadUsers() {
    try {
      const res = await fetch(
        `https://aaapd.org.et/gallery_backend/api/get_users.php?currentUserId=${currentUserId}`
      );
      const allUsers = await res.json();
      // Exclude the current user
      setUsers(allUsers.filter((u) => u.id !== currentUserId));
    } catch (err) {
      console.error("Failed to load users:", err);
    }
  }

  // Resolve and persist current user id if missing (use current email)
  async function resolveCurrentUserId() {
    const stored = JSON.parse(localStorage.getItem("currentUser") || "{}");
    if (stored?.id && Number(stored.id) > 0) return Number(stored.id);
    try {
      const res = await fetch(
        `https://aaapd.org.et/gallery_backend/api/get_users.php`
      );
      const all = await res.json();
      if (Array.isArray(all)) {
        const me = all.find(
          (u) =>
            (u.email || "").toLowerCase() === (currentEmail || "").toLowerCase()
        );
        if (me?.id) {
          const updated = { ...stored, id: me.id };
          localStorage.setItem("currentUser", JSON.stringify(updated));
          return Number(me.id);
        }
      }
    } catch (_) {}
    return null;
  }

  // 🔹 Helper: load messages for a conversation
  async function loadMessagesForConversation(conversationId) {
    if (!conversationId) return;

    try {
      const res = await fetch(
        `https://aaapd.org.et/gallery_backend/api/get_messages.php?conversationId=${conversationId}&userId=${currentUserId}`
      );
      console.log("Messages response status:", res.status);
      const msgs = await res.json();
      console.log("Messages loaded:", msgs);

      if (!Array.isArray(msgs)) {
        console.error("Messages response is not an array:", msgs);
        setMessages([]);
        return;
      }

      setMessages(msgs);
    } catch (err) {
      console.error("Failed to load messages:", err);
      setMessages([]);
    }
  }

  async function openConversation(conv) {
    setActiveConv(conv);
    setShowSidebar(false); // Hide sidebar on mobile when opening conversation
    console.log("Opening conversation:", conv);

    try {
      // 1) Load messages
      await loadMessagesForConversation(conv.id);

      // 2) Mark as read
      await fetch("https://aaapd.org.et/gallery_backend/api/mark_read.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ conversationId: conv.id, userId: currentUserId }),
      });

      // 3) Refresh unread counts
      loadConversations();
    } catch (err) {
      console.error("Failed to open conversation:", err);
    }
  }

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Handle file download (simpler approach without fetch)
  const handleFileDownload = (fileUrl, fileName) => {
    try {
      const link = document.createElement("a");
      link.href = fileUrl;
      link.download = fileName;
      link.target = "_blank";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      console.log(`Downloaded: ${fileName}`);
    } catch (error) {
      console.error("Download failed:", error);
      alert("Failed to download file");
    }
  };


  // Get file icon based on file type
  const getFileIcon = (fileType, fileName) => {
    if (!fileType && fileName) {
      const ext = fileName.split(".").pop().toLowerCase();
      if (["pdf"].includes(ext)) return "📄";
      if (["doc", "docx"].includes(ext)) return "📝";
      if (["xls", "xlsx"].includes(ext)) return "📊";
      if (["ppt", "pptx"].includes(ext)) return "📊";
      if (["zip", "rar", "7z"].includes(ext)) return "🗜";
      if (["mp3", "wav", "ogg"].includes(ext)) return "🎵";
      if (["mp4", "avi", "mkv"].includes(ext)) return "🎬";
    }

    if (fileType?.startsWith("application/pdf")) return "📄";
    if (fileType?.includes("word") || fileType?.includes("document")) return "📝";
    if (fileType?.includes("sheet") || fileType?.includes("excel")) return "📊";
    if (fileType?.includes("presentation") || fileType?.includes("powerpoint"))
      return "📊";
    if (fileType?.includes("zip") || fileType?.includes("compressed"))
      return "🗜";
    if (fileType?.startsWith("audio/")) return "🎵";
    if (fileType?.startsWith("video/")) return "🎬";

    return "📎";
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return "";
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  // Scroll to a specific message and highlight it
  const scrollToMessage = (messageId) => {
    const messageElement = document.getElementById(`message-${messageId}`);
    if (messageElement) {
      messageElement.scrollIntoView({ behavior: "smooth", block: "center" });
      setHighlightedMessageId(messageId);
      setTimeout(() => {
        setHighlightedMessageId(null);
      }, 2000);
    }
  };

  const toggleFullScreen = () => {
    if (!isFullScreen) {
      if (screenShareContainerRef.current?.requestFullscreen) {
        screenShareContainerRef.current.requestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
    setIsFullScreen(!isFullScreen);
  };

  const toggleMute = () => {
    if (screenStream) {
      const audioTracks = screenStream.getAudioTracks();
      audioTracks.forEach((track) => {
        track.enabled = !track.enabled;
      });
      setIsMuted(!isMuted);
    }
  };

  async function createConversationWith(targetUserIdOrEmail, userName) {
    // Ensure we have a valid current user id
    let uid = currentUserId || (await resolveCurrentUserId());
    if (!uid) {
      alert("Your session is missing an ID. Please re-login.");
      return;
    }

    let userId = targetUserIdOrEmail;
    if (typeof userId !== "number") {
      // Treat as email; find matching user id
      try {
        let all = users;
        if (!Array.isArray(all) || all.length === 0) {
          const res = await fetch(
            `https://aaapd.org.et/gallery_backend/api/get_users.php?currentUserId=${uid}`
          );
          all = await res.json();
        }
        const found = Array.isArray(all)
          ? all.find(
              (u) =>
                (u.email || "").toLowerCase() ===
                String(userId).toLowerCase()
            )
          : null;
        if (found?.id) {
          userId = found.id;
          userName = found.name || userName;
        } else {
          alert("User with that email was not found.");
          return;
        }
      } catch (_) {
        alert("Failed to find user by email");
        return;
      }
    }

    if (!userId) return;
    if (Number(userId) === Number(uid)) {
      alert("You can't chat with yourself.");
      return;
    }


    try {
      const response = await fetch(
        "https://aaapd.org.et/gallery_backend/api/create_conversation.php",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            memberIds: Array.from(
              new Set([Number(uid), Number(userId)].filter(Boolean))
            ),
            name: userName,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Failed to create conversation");
      }

      const conv = await response.json();
      await loadConversations();
      openConversation(conv);
    } catch (error) {
      console.error("Error creating conversation:", error);
      alert(`Error: ${error.message || "Failed to create conversation"}`);
    }
  }

  async function handleSend(e) {
    e.preventDefault();
    if (!activeConv || (!composing.trim() && !fileInput)) return;
    setSending(true);

    console.log(
      "Sending message - ConvID:",
      activeConv.id,
      "SenderID:",
      currentUserId
    );

    try {
      const formData = new FormData();
      formData.append("conversationId", activeConv.id);
      formData.append("senderId", currentUserId);
      formData.append("text", composing.trim());
      formData.append("replyTo", replyTo ? replyTo.id : "");

      if (fileInput) formData.append("file", fileInput);

      const res = await fetch(
        "https://aaapd.org.et/gallery_backend/api/send_message.php",
        {
          method: "POST",
          body: formData,
        }
      );

      console.log("Send message response status:", res.status);
      const msg = await res.json();
      console.log("Message sent:", msg);

      if (msg.status === "error") {
        throw new Error(msg.message);
      }

      // ✅ Reload from server so both sides stay in sync
      await loadMessagesForConversation(activeConv.id);

      setComposing("");
      setFileInput(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      setReplyTo(null);

      loadConversations();
    } catch (err) {
      console.error("Send message error:", err);
      alert("Failed to send message: " + err.message);
    } finally {
      setSending(false);
    }
  }

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) setFileInput(file);
  };

  const onRightClickMessage = (e, msg) => {
    e.preventDefault();
    setContextMessage(msg);
    const MENU_WIDTH = 180; // ~ w-40
    const PADDING = 8;
    const viewportWidth =
      window.innerWidth || document.documentElement.clientWidth;
    const viewportHeight =
      window.innerHeight || document.documentElement.clientHeight;
    const xLeft = Math.max(
      PADDING,
      Math.min(e.clientX - MENU_WIDTH, viewportWidth - MENU_WIDTH - PADDING)
    );
    const yClamped = Math.max(
      PADDING,
      Math.min(e.clientY, viewportHeight - 200)
    );
    setContextMenu({ x: xLeft, y: yClamped });
  };

  const handleContextEdit = () => {
    if (!contextMessage) return;
    setEditingMessageId(contextMessage.id);
    setEditingText(contextMessage.text);
    setContextMenu(null);
  };

  const handleContextDelete = () => {
    setMessageToDelete(contextMessage);
    setContextMenu(null);
    setDeleteConfirm(true);
  };

  async function deleteMessageConfirmed() {
    if (!messageToDelete) return;
    try {
      const resp = await fetch(
        `https://aaapd.org.et/gallery_backend/api/delete_message.php?id=${messageToDelete.id}`
      );
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || (data && data.status === "error")) {
        throw new Error(data?.message || "Failed to delete message");
      }

      // Optimistic remove
      setMessages((prev) => prev.filter((m) => m.id !== messageToDelete.id));
      loadConversations();


      // Refresh messages to ensure consistency
      if (activeConv?.id) {
        try {
          await loadMessagesForConversation(activeConv.id);
        } catch {}
      }
    } catch (err) {
      console.error(err);
      alert(err.message || "Failed to delete message");
    } finally {
      setDeleteConfirm(false);
      setContextMessage(null);
      setMessageToDelete(null);
    }
  }

  const handleContextReply = () => {
    if (!contextMessage) return;
    setReplyTo(contextMessage);
    setContextMenu(null);
  };

  async function saveEdit() {
    if (!editingText.trim()) return;
    try {
      const payload = {
        messageId: editingMessageId,
        message_id: editingMessageId,
        text: editingText.trim(),
        userId: currentUserId,
      };
      const resp = await fetch(
        "https://aaapd.org.et/gallery_backend/api/edit_message.php",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || (data && data.status === "error")) {
        throw new Error(data?.message || "Failed to edit message");
      }

      // Optimistic update
      setMessages((prev) =>
        prev.map((m) =>
          m.id === editingMessageId
            ? { ...m, text: editingText.trim(), edited: 1 }
            : m
        )
      );

      // Refresh from server to ensure consistency
      if (activeConv?.id) {
        try {
          await loadMessagesForConversation(activeConv.id);
        } catch {}
      }
    } catch (err) {
      console.error(err);
      alert(err.message || "Failed to edit message");
    } finally {
      setEditingMessageId(null);
      setEditingText("");
    }
  }

  const handleNewConversation = () => {
    const email = window.prompt("Enter contact email for one-to-one chat:");
    if (email) createConversationWith(email.trim());
  };

  const toggleGroupMember = (userId) => {
    setSelectedGroupMemberIds((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const toggleAddMember = (userId) => {
    setSelectedAddMemberIds((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  async function handleCreateGroup(e) {
    e.preventDefault();

    // Ensure current user id
    let uid = currentUserId || (await resolveCurrentUserId());
    if (!uid) {
      alert("Your session is missing an ID. Please re-login.");
      return;
    }

    const memberIds = Array.from(
      new Set(
        [Number(uid), ...selectedGroupMemberIds.map((id) => Number(id))].filter(
          Boolean
        )
      )
    );

    if (memberIds.length < 2) {
      alert("Please select at least one other member for the group.");
      return;
    }

    const trimmedName = (groupName || "").trim();
    if (!trimmedName) {
      alert("Please enter a group name.");
      return;
    }

    try {
      const response = await fetch(
        "https://aaapd.org.et/gallery_backend/api/create_conversation.php",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({ memberIds, name: trimmedName }),
        }
      );

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || "Failed to create group");
      }

      const conv = await response.json();
      await loadConversations();
      openConversation(conv);

      setShowGroupModal(false);
      setGroupName("");
      setSelectedGroupMemberIds([]);
    } catch (err) {
      console.error("Error creating group conversation:", err);
      alert(err.message || "Failed to create group conversation");
    }
  }

  async function handleAddMembers(e) {
    e.preventDefault();


    if (!activeConv?.id) {
      alert("No active conversation selected.");
      return;
    }

    const newMemberIds = Array.from(
      new Set(selectedAddMemberIds.map((id) => Number(id)).filter(Boolean))
    );

    if (newMemberIds.length === 0) {
      alert("Please select at least one member to add.");
      return;
    }

    try {
      const response = await fetch(
        "https://aaapd.org.et/gallery_backend/api/add_conversation_members.php",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            conversationId: activeConv.id,
            memberIds: newMemberIds,
          }),
        }
      );

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || "Failed to add members");
      }

      const updatedConv = await response.json();
      await loadConversations();
      setActiveConv(updatedConv);

      setShowAddMembersModal(false);
      setSelectedAddMemberIds([]);
    } catch (err) {
      console.error("Error adding members to conversation:", err);
      alert(err.message || "Failed to add members");
    }
  }

  const filteredConversations = conversations.filter(
    (c) =>
      (c.name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (Array.isArray(c.members) &&
        c.members.some((m) =>
          (m || "").toLowerCase().includes(searchTerm.toLowerCase())
        ))
  );

  const getOtherMember = (conv) => {
    if (!conv || !conv.participants)
      return { id: null, email: "", name: "" };
    const other = conv.participants.find((p) => p.id !== currentUserId);
    return other || { id: null, email: "", name: conv.name };
  };

  const isGroup = (conv) =>
    (Array.isArray(conv?.participants) && conv.participants.length > 2) ||
    (conv?.name || "").toLowerCase().includes("group");

  const getConvDisplayName = (conv) =>
    isGroup(conv) ? conv?.name || "Group" : getOtherMember(conv).name;

  const getConvSubtitle = (conv) => {
    if (isGroup(conv)) {
      if ((conv?.name || "").toLowerCase() === "global group") {
        const total =
          (Array.isArray(users) ? users.length : 0) + (currentUserId ? 1 : 0);
        return `${total} members`;
      }
      return `${conv?.participants?.length || 0} members`;
    }
    return getOtherMember(conv).email || "";
  };

  const getSenderInitial = (msg) => {
    if (msg?.senderId === currentUserId)
      return (currentUser?.name || "You").charAt(0).toUpperCase();
    const p = (activeConv?.participants || []).find(
      (p) => p.id === msg?.senderId
    );
    return (p?.name || msg?.senderName || "?").charAt(0).toUpperCase();
  };

  const isDirectConversation = (conv) =>
    Array.isArray(conv?.participants) && conv.participants.length === 2;

  const getAvailableUsersForActiveConv = () => {
    if (!activeConv || !Array.isArray(activeConv.participants)) return users || [];
    const existingIds = new Set(activeConv.participants.map((p) => p.id));
    return (users || []).filter((u) => !existingIds.has(u.id));
  };

  // ===== RENDER =====
  return (
    <div className="flex h-[calc(100vh-80px)] font-sans bg-gray-50 text-gray-800 relative">
      {/* Mobile Overlay */}
      {showSidebar && activeConv && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setShowSidebar(false)}
        />
      )}


      {/* Sidebar */}
      <aside
        className={`
          w-full sm:w-80 lg:w-80 flex-shrink-0 bg-white border-r border-gray-200 flex flex-col
          fixed lg:relative inset-y-0 left-0 z-40 transform transition-transform duration-300 ease-in-out
          ${showSidebar ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
          ${activeConv ? "lg:flex" : "flex"}
        `}
      >
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-teal-700 flex items-center gap-2">
              <MessageSquare size={24} /> Chats
            </h2>
            <button
              onClick={() => setShowSidebar(false)}
              className="lg:hidden p-2 hover:bg-gray-100 rounded-lg"
            >
              <X size={20} />
            </button>
          </div>
          <div className="relative mt-4">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              size={18}
            />
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-gray-100 border border-gray-200 rounded-lg pl-10 pr-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={() => setShowGroupModal(true)}
              className="flex-1 text-xs sm:text-sm px-3 py-2 rounded-lg bg-teal-600 text-white hover:bg-teal-700"
            >
              Create New Group
            </button>
          </div>
        </div>

        <div className="flex-1 p-4 overflow-y-auto">
          <ul className="space-y-2">
            {[
              ...conversations.map((c) => ({
                type: "conversation",
                data: c,
                key: `conv-${c.id}`,
              })),
              ...users
                .filter(
                  (u) =>
                    !conversations.some(
                      (c) =>
                        isDirectConversation(c) &&
                        c.participants &&
                        c.participants.some((p) => p.id === u.id)
                    )
                )
                .map((u) => ({
                  type: "user",
                  data: u,
                  key: `user-${u.id}`,
                })),
            ]
              .filter((item) => {
                const name =
                  item.type === "conversation"
                    ? (getConvDisplayName(item.data) || "").toLowerCase()
                    : (item.data.name || "").toLowerCase();
                const email =
                  item.type === "conversation"
                    ? (getConvSubtitle(item.data) || "").toLowerCase()
                    : (item.data.email || "").toLowerCase();
                const term = (searchTerm || "").toLowerCase();
                return name.includes(term) || email.includes(term);
              })
              .map((item) => {
                const isActive =
                  item.type === "conversation"
                    ? activeConv?.id === item.data.id
                    : false;

                const name =
                  item.type === "conversation"
                    ? getConvDisplayName(item.data)
                    : item.data.name;
                const email =
                  item.type === "conversation"
                    ? getConvSubtitle(item.data)
                    : item.data.email;


                return (
                  <li
                    key={item.key}
                    onClick={() =>
                      item.type === "conversation"
                        ? openConversation(item.data)
                        : createConversationWith(item.data.id, item.data.name)
                    }
                    onContextMenu={(e) => {
                      if (item.type === "conversation") {
                        e.preventDefault();
                        const MENU_WIDTH = 180;
                        const PADDING = 8;
                        const viewportWidth =
                          window.innerWidth ||
                          document.documentElement.clientWidth;
                        const viewportHeight =
                          window.innerHeight ||
                          document.documentElement.clientHeight;
                        const xRight = Math.max(
                          PADDING,
                          Math.min(
                            e.clientX,
                            viewportWidth - MENU_WIDTH - PADDING
                          )
                        );
                        const yClamped = Math.max(
                          PADDING,
                          Math.min(e.clientY, viewportHeight - 200)
                        );
                        setConvToDelete(item.data);
                        setConvContextMenu({ x: xRight, y: yClamped });
                      }
                    }}
                    className={`p-2 rounded-lg cursor-pointer flex items-center gap-3 hover:bg-gray-100 ${
                      isActive ? "bg-teal-100" : ""
                    }`}
                  >
                    {/* Avatar */}
                    <div className="relative">
                      <div className="w-10 h-10 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center font-bold">
                        {name?.charAt(0).toUpperCase()}
                      </div>
                      {/* Online / New user indicator */}
                      {item.type === "conversation" ? (
                        <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white rounded-full"></span>
                      ) : (
                        <span className="absolute bottom-0 right-0 w-3 h-3 bg-teal-500 border-2 border-white rounded-full"></span>
                      )}
                    </div>

                    {/* Name & Email */}
                    <div className="flex-1 flex flex-col truncate">
                      <div className="flex justify-between items-center">
                        <span className="font-semibold text-gray-800 truncate">
                          {name}
                        </span>
                        {item.type === "conversation" &&
                          item.data.unreadCount > 0 && (
                            <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                              {item.data.unreadCount}
                            </span>
                          )}
                      </div>
                      <span className="text-xs text-gray-400 truncate">
                        {email}
                      </span>
                    </div>
                  </li>
                );
              })}
          </ul>
        </div>
      </aside>


      {/* New Group Modal */}
      {showGroupModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Create Group
              </h3>
              <button
                type="button"
                onClick={() => {
                  setShowGroupModal(false);
                  setGroupName("");
                  setSelectedGroupMemberIds([]);
                }}
                className="p-2 text-gray-500 hover:text-gray-800 rounded-lg hover:bg-gray-100"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleCreateGroup} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Group Name
                </label>
                <input
                  type="text"
                  value={groupName}
                  onChange={(e) => setGroupName(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="Enter group name"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Members
                </label>
                <div className="max-h-56 overflow-y-auto border rounded-lg divide-y">
                  {users.length === 0 ? (
                    <p className="text-xs text-gray-500 px-3 py-2">
                      No other users available.
                    </p>
                  ) : (
                    users.map((u) => (
                      <label
                        key={u.id}
                        className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-gray-50"
                      >
                        <input
                          type="checkbox"
                          className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                          checked={selectedGroupMemberIds.includes(u.id)}
                          onChange={() => toggleGroupMember(u.id)}
                        />
                        <span className="font-medium text-gray-800 truncate">
                          {u.name || u.email}
                        </span>
                        {u.name && (
                          <span className="text-xs text-gray-400 truncate">
                            {u.email}
                          </span>
                        )}
                      </label>
                    ))
                  )}
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowGroupModal(false);
                    setGroupName("");
                    setSelectedGroupMemberIds([]);
                  }}
                  className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm rounded-lg bg-teal-600 text-white hover:bg-teal-700"
                >
                  Create Group
                </button>
              </div>
            </form>
          </div>
        </div>
      )}


      {/* Add Members Modal */}
      {showAddMembersModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Add Members
              </h3>
              <button
                type="button"
                onClick={() => {
                  setShowAddMembersModal(false);
                  setSelectedAddMemberIds([]);
                }}
                className="p-2 text-gray-500 hover:text-gray-800 rounded-lg hover:bg-gray-100"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleAddMembers} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Members to Add
                </label>
                <div className="max-h-56 overflow-y-auto border rounded-lg divide-y">
                  {getAvailableUsersForActiveConv().length === 0 ? (
                    <p className="text-xs text-gray-500 px-3 py-2">
                      No additional users available to add.
                    </p>
                  ) : (
                    getAvailableUsersForActiveConv().map((u) => (
                      <label
                        key={u.id}
                        className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-gray-50"
                      >
                        <input
                          type="checkbox"
                          className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                          checked={selectedAddMemberIds.includes(u.id)}
                          onChange={() => toggleAddMember(u.id)}
                        />
                        <span className="font-medium text-gray-800 truncate">
                          {u.name || u.email}
                        </span>
                        {u.name && (
                          <span className="text-xs text-gray-400 truncate">
                            {u.email}
                          </span>
                        )}
                      </label>
                    ))
                  )}
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddMembersModal(false);
                    setSelectedAddMemberIds([]);
                  }}
                  className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm rounded-lg bg-teal-600 text-white hover:bg-teal-700"
                >
                  Add Members
                </button>
              </div>
            </form>
          </div>
        </div>
      )}


      {/* Main Chat Area */}
      <main className="flex-1 flex flex-col bg-gray-100 w-full lg:w-auto">
        {activeConv ? (
          <>
            <header className="flex items-center justify-between p-3 sm:p-4 bg-white border-b border-gray-200 sticky top-0 z-10">
              <div className="flex items-center gap-2 sm:gap-4 flex-1">
                {/* Mobile back button */}
                <button
                  onClick={() => {
                    setShowSidebar(true);
                    setActiveConv(null);
                  }}
                  className="lg:hidden p-2 hover:bg-gray-100 rounded-lg"
                >
                  <CornerUpLeft size={20} />
                </button>
                {/* Desktop menu button */}
                <button
                  onClick={() => setShowSidebar(!showSidebar)}
                  className="hidden lg:block p-2 hover:bg-gray-100 rounded-lg"
                >
                  <MessageSquare size={20} />
                </button>
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-teal-100 text-teal-600 flex items-center justify-center font-bold text-base sm:text-lg">
                  {getConvDisplayName(activeConv)?.charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <h3 className="font-bold text-base sm:text-lg text-gray-800 truncate">
                    {getConvDisplayName(activeConv)}
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-500 truncate">
                    {getConvSubtitle(activeConv)}
                  </p>
                  {isGroup(activeConv) && (
                    <div className="mt-2 flex justify-center">
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedAddMemberIds([]);
                          setShowAddMembersModal(true);
                        }}
                        className="px-3 py-1.5 text-xs sm:text-sm rounded-lg bg-teal-600 text-white hover:bg-teal-700"
                      >
                        Add Members
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </header>

            <div className="flex-1 p-3 sm:p-4 md:p-6 overflow-y-auto">
              <div className="space-y-3 sm:space-y-4 md:space-y-6">
                {messages.map((msg) => {
                  const mine = msg.senderId === currentUserId;
                  const isEdited = msg.edited === 1 || msg.edited === true || msg.edited === "1";

                  const isEditing = editingMessageId === msg.id;
                  const repliedMsg = msg.replyTo
                    ? messages.find((m) => m.id === msg.replyTo)
                    : null;


                  return (
                    <div
                      key={msg.id}
                      id={`message-${msg.id}`}
                      className={`flex gap-2 sm:gap-3 ${
                        mine ? "justify-end" : "justify-start"
                      } transition-all duration-300 ${
                        highlightedMessageId === msg.id ? "scale-105" : ""
                      }`}
                    >
                      {!mine && (
                        <div
                          className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-gray-200 text-gray-600 flex items-center justify-center font-bold text-xs sm:text-sm flex-shrink-0"
                          title={msg.senderName || ""}
                        >
                          {getSenderInitial(msg)}
                        </div>
                      )}
                      <div
                        className={`max-w-[85%] sm:max-w-md w-fit ${
                          mine ? "order-1" : ""
                        }`}
                      >
                        <div
                          onContextMenu={(e) => onRightClickMessage(e, msg)}
                          className={`relative p-2 sm:p-3 rounded-lg text-sm sm:text-base transition-all duration-300 ${
                            mine
                              ? "bg-teal-600 text-white rounded-br-none"
                              : "bg-white text-gray-800 rounded-bl-none shadow-sm"
                          } ${
                            highlightedMessageId === msg.id
                              ? "ring-2 ring-yellow-400 shadow-lg"
                              : ""
                          }`}
                        >
                          {repliedMsg && (
                            <div
                              onClick={() => scrollToMessage(repliedMsg.id)}
                              className="border-l-2 border-teal-200 pl-2 mb-2 text-xs sm:text-sm opacity-80 cursor-pointer hover:opacity-100 hover:bg-white/10 rounded p-1 -ml-1 transition-all"
                              title="Click to view original message"
                            >
                              <p className="font-semibold">
                                {repliedMsg.senderId === currentUserId
                                  ? "You"
                                  : getOtherMember(activeConv).name}
                              </p>
                              <p className="truncate">
                                {repliedMsg.text || "Attachment"}
                              </p>
                            </div>
                          )}
                          {isEditing ? (
                            <div className="w-full sm:w-80">
                              <textarea
                                value={editingText}
                                onChange={(e) =>
                                  setEditingText(e.target.value)
                                }
                                className="w-full bg-white/20 p-2 rounded-md text-sm text-inherit focus:outline-none"
                                rows={3}
                              />
                              <div className="flex justify-end gap-2 mt-2">
                                <button
                                  onClick={() => setEditingMessageId(null)}
                                  className="text-xs px-2 py-1 rounded hover:bg-white/10"
                                >
                                  Cancel
                                </button>
                                <button
                                  onClick={saveEdit}
                                  className="text-xs px-2 py-1 rounded bg-white/30 hover:bg-white/40"
                                >
                                  Save
                                </button>
                              </div>
                            </div>
                          ) : (
                            <>
                              {msg.text && ( 

                                <p className="whitespace-pre-wrap">
                                  {msg.text}
                                </p>
                              )}
                              {msg.fileUrl &&
                                msg.fileType?.startsWith("image/") && (
                                  <img
                                    src={msg.fileUrl}
                                    alt={msg.fileName}
                                    onClick={() =>
                                      setImagePreviewUrl(msg.fileUrl)
                                    }
                                    className="mt-2 rounded-lg max-w-[200px] sm:max-w-xs cursor-pointer"
                                  />
                                )}
                              {msg.fileUrl &&
                                !msg.fileType?.startsWith("image/") && (
                                  <div
                                    className="mt-2 bg-white/10 rounded-lg overflow-hidden cursor-pointer hover:bg-white/20 transition-colors"
                                    onClick={() =>
                                      handleFileDownload(
                                        msg.fileUrl,
                                        msg.fileName
                                      )
                                    }
                                  >
                                    <div className="flex items-center gap-3 p-3">
                                      <div className="text-3xl flex-shrink-0">
                                        {getFileIcon(
                                          msg.fileType,
                                          msg.fileName
                                        )}
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium truncate">
                                          {msg.fileName}
                                        </p>
                                        <p className="text-xs opacity-70">
                                          {msg.fileSize
                                            ? formatFileSize(msg.fileSize)
                                            : "Document"}
                                        </p>
                                      </div>
                                      <div className="flex-shrink-0 text-white/70">
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          width="20"
                                          height="20"
                                          viewBox="0 0 24 24"
                                          fill="none"
                                          stroke="currentColor"
                                          strokeWidth="2"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        >
                                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                          <polyline points="7 10 12 15 17 10"></polyline>
                                          <line
                                            x1="12"
                                            y1="15"
                                            x2="12"
                                            y2="3"
                                          ></line>
                                        </svg>
                                      </div>
                                    </div>
                                  </div>
                                )}
                            </>
                          )}
                        </div>
                        <div
  className={`text-[10px] sm:text-xs mt-1 flex items-center gap-1 sm:gap-2 ${
    mine ? "justify-end" : "justify-start"
  }`}
>
  <span className="text-gray-400">
    {new Date(msg.createdAt).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    })}
  </span>
  {isEdited && (
    <span className="text-gray-400 text-xs"></span>
  )}
</div>

                      </div>
                    </div>
                  );
                })}
              </div>
              <div ref={messagesEndRef} />
            </div>

            <div className="p-3 sm:p-4 bg-white border-t border-gray-200">
              {replyTo && (
                <div className="bg-gray-100 p-2 rounded-t-lg flex justify-between items-center text-xs sm:text-sm">
                  <div className="border-l-2 border-teal-500 pl-2">
                    <p className="font-semibold">
                      Replying to{" "}
                      {replyTo.senderId === currentUserId
                        ? "yourself"
                        : getOtherMember(activeConv).name}
                    </p>
                    <p className="text-gray-600 truncate">
                      {replyTo.text || "Attachment"}
                    </p>
                  </div>
                  <button
                    onClick={() => setReplyTo(null)}
                    className="p-1 rounded-full hover:bg-gray-200"
                  >
                    <X size={16} />
                  </button>
                </div>
              )}
              <form onSubmit={handleSend} className="relative">
                <textarea
                  value={composing}
                  onChange={(e) => setComposing(e.target.value)}
                  placeholder="Type a message..."
                  className="w-full bg-gray-100 border border-gray-200 rounded-lg p-2 sm:p-3 pr-20 sm:pr-24 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
                  rows={1}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) handleSend(e);
                  }}
                  disabled={sending}
                />
                <div className="absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 flex items-center gap-1 sm:gap-2">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="p-1.5 sm:p-2 rounded-full text-gray-500 hover:bg-gray-200 hover:text-teal-600"
                  >
                    <Paperclip size={18} className="sm:w-5 sm:h-5" />
                  </button>
                  <button
                    type="submit"
                    disabled={sending || (!composing.trim() && !fileInput)}
                    className="p-1.5 sm:p-2 rounded-full bg-teal-600 text-white disabled:bg-teal-300 hover:bg-teal-700 transition-colors"
                  >
                    <Send size={18} className="sm:w-5 sm:h-5" />
                  </button>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    className="hidden"
                  />
                </div>
              </form>
              {fileInput && (
                <div className="text-xs text-gray-500 mt-2 flex items-center gap-2">

                  Attached: {fileInput.name}
                  <button
                    onClick={() => {
                      setFileInput(null);
                      if (fileInputRef.current)
                        fileInputRef.current.value = "";
                    }}
                    className="text-red-500 hover:underline"
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-center text-gray-500 p-4">
            <MessageSquare size={48} className="mb-4 text-gray-300" />

            <h3 className="text-lg sm:text-xl font-semibold">
              Select a conversation
            </h3>
            <p className="text-sm sm:text-base">
              Choose from an existing conversation or start a new one.
            </p>
            <button
              onClick={() => setShowSidebar(true)}
              className="mt-4 lg:hidden px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
            >
              View Conversations
            </button>
          </div>
        )}
      </main>

      {/* Context Menu */}
      {contextMenu && contextMessage && (
        <ul
          style={{ top: contextMenu.y, left: contextMenu.x }}
          className="fixed z-50 bg-white shadow-lg rounded-lg py-2 w-36 sm:w-40 border border-gray-200 text-xs sm:text-sm"
        >
          <li
            onClick={handleContextReply}
            className="px-4 py-2 hover:bg-gray-100 cursor-pointer flex items-center gap-2"
          >
            <Reply size={16} /> Reply
          </li>
          {contextMessage.senderId === currentUserId && (
            <>
              <li
                onClick={handleContextEdit}
                className="px-4 py-2 hover:bg-gray-100 cursor-pointer flex items-center gap-2"
              >
                <Edit3 size={16} /> Edit
              </li>
              <li
                onClick={handleContextDelete}
                className="px-4 py-2 hover:bg-red-50 text-red-600 cursor-pointer flex items-center gap-2"
              >
                <Trash2 size={16} /> Delete
              </li>
            </>
          )}
        </ul>
      )}

      {convContextMenu && convToDelete && (
        <ul
          style={{ top: convContextMenu.y, left: convContextMenu.x }}
          className="fixed z-50 bg-white shadow-lg rounded-lg py-2 w-40 border border-gray-200 text-xs sm:text-sm"
        >
          <li
            onClick={() => {
              setConvContextMenu(null);
              setConvDeleteConfirm(true);
            }}
            className="px-4 py-2 hover:bg-red-50 text-red-600 cursor-pointer flex items-center gap-2"
          >
            <Trash2 size={16} /> Delete conversation
          </li>
        </ul>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-sm">
            <h3 className="text-lg font-bold">Delete Message</h3>
            <p className="text-gray-600 my-4">
              Are you sure you want to delete this message? This action cannot
              be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm(false)}
                className="px-4 py-2 rounded-lg bg-gray-200 hover:bg-gray-300 font-semibold"
              >
                Cancel
              </button>
              <button
                onClick={deleteMessageConfirmed}
                className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 font-semibold"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}


      {convDeleteConfirm && convToDelete && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-sm">
            <h3 className="text-lg font-bold">Delete Conversation</h3>
            <p className="text-gray-600 my-4">
              Are you sure you want to delete this conversation and all its
              messages? This cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setConvDeleteConfirm(false)}
                className="px-4 py-2 rounded-lg bg-gray-200 hover:bg-gray-300 font-semibold"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  try {
                    const resp = await fetch(
                      "https://aaapd.org.et/gallery_backend/api/delete_conversation.php",
                      {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({
                          conversationId: convToDelete.id,
                        }),
                      }
                    );
                    const data = await resp.json().catch(() => ({}));
                    if (!resp.ok || (data && data.status === "error")) {
                      throw new Error(
                        data?.message || "Failed to delete conversation"
                      );
                    }
                    setConversations((prev) =>
                      prev.filter((c) => c.id !== convToDelete.id)
                    );
                    if (activeConv?.id === convToDelete.id) {
                      setActiveConv(null);
                      setMessages([]);
                    }
                    setConvToDelete(null);
                    setConvDeleteConfirm(false);
                  } catch (err) {
                    alert(err.message || "Failed to delete conversation");
                  }
                }}
                className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 font-semibold"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Preview Modal */}
      {imagePreviewUrl && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setImagePreviewUrl(null)}
        >
          <button
            onClick={() => setImagePreviewUrl(null)}
            className="absolute top-4 right-4 text-white p-2 rounded-full bg-white/10 hover:bg-white/20"
          >
            <X size={24} />
          </button>
          <img
            src={imagePreviewUrl}
            alt="Preview"
            className="max-w-full max-h-full rounded-lg"
          />
        </div>
      )}
    </div>
  );
}
